import L from "leaflet";
import EventBus from "@/utils/eventBus";

const ESTILO_DESENHO = {
  color: "#fb8c00",
  weight: 2,
  fillColor: "#ffb74d",
  fillOpacity: 0.18,
  dashArray: "4 4",
};

/**
 * Desenho de uma área poligonal (por cliques) no mapa da tela de candidatos para selecionar
 * todos os cadastros da seção ativa que estão dentro dela (a consulta é feita no servidor).
 *
 * A tela que usa o mixin deve fornecer:
 *  - `_candidatosLeafletMap`: o objeto Leaflet do mapa;
 *  - `aplicarFiltroPoligono("lat,lng;...")`: o servidor filtra todos os cadastros da seção ativa
 *    pelo polígono e a tabela marca os que estão dentro;
 *  - `removerFiltroPoligono()`: tira esse filtro da seção ativa;
 *  - `filtroAtivoNaAba`: true se a seção ativa está filtrada por área.
 */
export default {
  data: () => ({
    areaDesenho: {
      ativo: false,
      vertices: [],
      poligono: false,
    },
  }),
  computed: {
    areaPodeConcluir() {
      return this.areaDesenho.ativo && this.areaDesenho.vertices.length >= 3;
    },
  },
  watch: {
    mainTab() {
      this.limparAreaDesenho();
    },
  },
  beforeDestroy() {
    this.limparAreaDesenho();
  },
  methods: {
    areaMapa() {
      return this._candidatosLeafletMap
        || (this.$refs.candidatosMap && this.$refs.candidatosMap.mapObject)
        || null;
    },
    alternarDesenhoArea() {
      if (this.areaDesenho.ativo) {
        this.cancelarDesenhoArea();
        return;
      }
      const map = this.areaMapa();
      if (!map) return;
      this.limparAreaDesenho();
      this.areaDesenho.ativo = true;
      this._areaClickBound = this._aoClicarMapaArea.bind(this);
      this._areaMoveBound = this._aoMoverMapaArea.bind(this);
      map.on("click", this._areaClickBound);
      map.on("mousemove", this._areaMoveBound);
      EventBus.$emit("show-alert", {
        tipo: "info",
        mensagem: this.$t("candidate.map.areaInstruction"),
        duracao: 6000,
      });
    },
    _aoClicarMapaArea(e) {
      const map = this.areaMapa();
      if (!this.areaDesenho.ativo || !map || !e || !e.latlng) return;
      const { lat, lng } = e.latlng;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      this.areaDesenho.vertices.push({ lat, lng });
      const marcador = L.circleMarker([lat, lng], {
        radius: 6,
        color: ESTILO_DESENHO.color,
        weight: ESTILO_DESENHO.weight,
        fillColor: ESTILO_DESENHO.fillColor,
        fillOpacity: 1,
      }).addTo(map);
      this._areaMarcadores = (this._areaMarcadores || []).concat(marcador);
      this._sincronizarSegmentosArea();
    },
    _sincronizarSegmentosArea() {
      const map = this.areaMapa();
      if (!map) return;
      if (this._areaSegmentos) {
        map.removeLayer(this._areaSegmentos);
        this._areaSegmentos = null;
      }
      const vertices = this.areaDesenho.vertices;
      if (vertices.length >= 2) {
        this._areaSegmentos = L.polyline(vertices.map((v) => [v.lat, v.lng]), ESTILO_DESENHO).addTo(map);
      }
    },
    _aoMoverMapaArea(e) {
      const map = this.areaMapa();
      const vertices = this.areaDesenho.vertices;
      if (!this.areaDesenho.ativo || !map || !e || !e.latlng || !vertices.length) return;
      const ultimo = vertices[vertices.length - 1];
      const pontos = [[ultimo.lat, ultimo.lng], [e.latlng.lat, e.latlng.lng]];
      if (this._areaElastico) {
        this._areaElastico.setLatLngs(pontos);
      } else {
        this._areaElastico = L.polyline(pontos, {
          ...ESTILO_DESENHO,
          dashArray: "6 6",
          opacity: 0.85,
        }).addTo(map);
      }
    },
    desfazerVerticeArea() {
      const map = this.areaMapa();
      if (!this.areaDesenho.vertices.length) return;
      this.areaDesenho.vertices.pop();
      const marcador = this._areaMarcadores && this._areaMarcadores.pop();
      if (marcador && map) map.removeLayer(marcador);
      this._sincronizarSegmentosArea();
      if (!this.areaDesenho.vertices.length && this._areaElastico && map) {
        map.removeLayer(this._areaElastico);
        this._areaElastico = null;
      }
    },
    cancelarDesenhoArea() {
      this.limparAreaDesenho();
    },
    /** Fecha o polígono e pede ao servidor os cadastros da seção ativa que estão dentro dele. */
    concluirDesenhoArea() {
      if (!this.areaPodeConcluir) return;
      const map = this.areaMapa();
      const vertices = this.areaDesenho.vertices.map((v) => ({ lat: v.lat, lng: v.lng }));
      this._desligarEscutasArea();
      this._removerCamadasEfemerasArea();
      this.areaDesenho.ativo = false;
      if (map) {
        this._areaPoligono = L.polygon(vertices.map((v) => [v.lat, v.lng]), {
          ...ESTILO_DESENHO,
          dashArray: null,
        }).addTo(map);
      }
      this.areaDesenho.poligono = true;

      const poligono = vertices.map((v) => `${v.lat},${v.lng}`).join(";");
      this.aplicarFiltroPoligono(poligono);
    },
    _desligarEscutasArea() {
      const map = this.areaMapa();
      if (!map) return;
      if (this._areaClickBound) map.off("click", this._areaClickBound);
      if (this._areaMoveBound) map.off("mousemove", this._areaMoveBound);
      this._areaClickBound = null;
      this._areaMoveBound = null;
    },
    _removerCamadasEfemerasArea() {
      const map = this.areaMapa();
      if (map) {
        (this._areaMarcadores || []).forEach((m) => map.removeLayer(m));
        if (this._areaSegmentos) map.removeLayer(this._areaSegmentos);
        if (this._areaElastico) map.removeLayer(this._areaElastico);
      }
      this._areaMarcadores = [];
      this._areaSegmentos = null;
      this._areaElastico = null;
    },
    /** Remove desenho em andamento e o polígono final (a seleção da tabela é mantida). */
    limparAreaDesenho() {
      this._desligarEscutasArea();
      this._removerCamadasEfemerasArea();
      const map = this.areaMapa();
      if (map && this._areaPoligono) map.removeLayer(this._areaPoligono);
      this._areaPoligono = null;
      this.areaDesenho.ativo = false;
      this.areaDesenho.vertices = [];
      this.areaDesenho.poligono = false;
    },
    /** "Limpar área": tira o polígono do mapa e o filtro da seção ativa (a seleção é mantida). */
    limparAreaCompleta() {
      this.limparAreaDesenho();
      this.removerFiltroPoligono();
    },
  },
};
